/**
 * @fileoverview The ONE way a web app configures its browser logger (BROWSER-TELEMETRY-001 P3).
 *
 * Ten apps import the `logger` proxy from this package; that proxy resolves to a SINGLETON, so the
 * configuration has to happen once, at bootstrap, before any module logs — and it has to attach to
 * a singleton that may already exist (a module that logged at import time). `createAppLogger` owns
 * both cases and the fleet's shipping policy: WARNING and above go to the API's client-log ingest
 * through the app's own api-client, INFO ships for sampled page traces, console output is a
 * development affordance, every uncaught error and unhandled rejection on the page is captured
 * into the same pipeline (`./captureGlobalErrors.js`, Q-UMB-13), and the page's Core Web Vitals are
 * measured and sent through it too (`./collectWebVitals.js`), as is every `logger.timing()` (the
 * ONE client timing record, F-TOCONTAIN-3 U2c) — sampled by the page trace, held until
 * the page's consent posture is known, withheld on a consent-governed page until the visitor grants
 * it (`./telemetryConsent.js`). One call, so every web entry gets the whole of it: the ten app
 * bundles call it from `apps/<app>/src/core/config/logger.js`, which `main.jsx` imports first, and
 * the nine marketing entries from `portals/marketing-website/telemetry.js`, which that entry's
 * `main.jsx` imports first (`browserTelemetryMounted` proves both).
 *
 * @module createAppLogger
 */

import * as webVitalsLibrary from 'web-vitals';
import { createLogger } from './Logger.js';
import { LOG_LEVEL } from './constants.js';
import { ServerTransport } from './transports/server.js';
import { installGlobalErrorCapture } from './captureGlobalErrors.js';
import { installWebVitalsCollector, toVitalRecord } from './collectWebVitals.js';
import { isProduction } from './utils/environment.js';
import { getTelemetryConsent, TELEMETRY_CONSENT } from './telemetryConsent.js';

/**
 * The page a vital was measured on: origin + path, NEVER the query or fragment — those carry
 * reset tokens, e-mail addresses and search terms, and a vital needs none of them.
 * @param {Object|null} target - the window
 * @returns {string|undefined}
 */
function pageUrl(target) {
  const location = target?.location;
  return location ? `${location.origin}${location.pathname}` : undefined;
}

/**
 * @param {Object} options
 * @param {string} options.app - the web app name, e.g. `'planner-web'` (lands on every entry as `app`)
 * @param {string} [options.surface] - which of the app's entries this is, when it has more than one
 *   (`'marketing'` for `marketing.html`); lands on every entry as `surface`, so a public-site error
 *   and a tenant-app error of the same app are told apart in Loki
 * @param {Object} options.client - the app's `@puffin-core/api-client` instance; a public marketing
 *   entry, which may not bundle axios, passes {@link createFetchLogClient} over its fetch seam
 * @param {boolean} [options.production=isProduction()] - console off + ship in production
 * @param {string} [options.level] - logger threshold (`LOG_LEVEL`); defaults to the env-derived level
 * @param {Object} [options.ship] - server-transport overrides: `minLevel`, `shipInfo` (`SHIP_INFO`), `endpoint`,
 *   `baseURL`, `batchSize`, `batchInterval`, `maxBuffer`, …
 * @param {EventTarget|null} [options.target=globalThis] - the window: where uncaught errors are
 *   captured and the page's hide events are observed; `null` for a non-browser host
 * @param {Object|null} [options.webVitals] - the `web-vitals` module; injected only by tests, `null`
 *   disables the collector
 * @returns {import('./Logger.js').Logger} the configured singleton
 */
export function createAppLogger({
  app,
  surface,
  client,
  production = isProduction(),
  level,
  ship = {},
  target = globalThis,
  webVitals = webVitalsLibrary,
} = {}) {
  if (!app || typeof app !== 'string') {
    throw new TypeError('createAppLogger requires `app` (the web app name, e.g. "planner-web")');
  }
  if (!client || typeof client.post !== 'function') {
    throw new TypeError(
      "createAppLogger requires `client` (the app's @puffin-core/api-client instance)",
    );
  }
  const logger = createLogger();
  logger.configure({
    consoleEnabled: !production,
    ...(level ? { level } : {}),
  });
  logger.setContext(surface ? { app, surface } : { app });
  // Idempotent per page: a previous ServerTransport (a second bootstrap call, HMR) is replaced,
  // never stacked — stacking would ship every entry twice.
  for (const transport of [...logger.transports]) {
    if (transport instanceof ServerTransport) {
      transport.destroy();
      logger.removeTransport(transport);
    }
  }
  const shipper = new ServerTransport({ ...ship, client, target });
  logger.addTransport(shipper);
  /** A measurement leaves the page only for a SAMPLED page trace (one sampling decision per page). */
  const shipSampled = (entry) => {
    if (!entry.sampled) return;
    shipper.enqueue({ ...entry, url: pageUrl(target) });
  };
  // Replaces any earlier installation on the same target, so a second bootstrap reports once.
  installGlobalErrorCapture({ logger, target });
  // A vital is a measurement, not a log line: built without the logger's verbosity threshold
  // (WARNING in production) and handed straight to the shipper, sent only for a SAMPLED page trace.
  installWebVitalsCollector({
    library: webVitals,
    host: target,
    beforeHide: (hook) => shipper.beforeHide(hook),
    onError: (registrar, err) =>
      logger.warn('Web vitals collector could not observe a metric', {
        registrar,
        error: err instanceof Error ? err.message : String(err),
      }),
    ship: (metric) => {
      const { message, data } = toVitalRecord(metric);
      shipSampled(logger.createEntry(LOG_LEVEL.INFO, message, data, { source: 'web-vital' }));
    },
  });
  // F-TOCONTAIN-3 U2c: `logger.timing()` is a measurement too — the same sampled-trace decision,
  // and the same consent posture as a vital (a timing measures the visitor's experience), decided
  // at the moment it is recorded: a consent-governed page ships it only once consent is GRANTED
  // (a pending or declined decision withholds it).
  logger.setMeasurementSink((entry) => {
    const { governed, decision } = getTelemetryConsent();
    if (governed && decision !== TELEMETRY_CONSENT.GRANTED) return;
    shipSampled(entry);
  });
  return logger;
}
